import { z } from 'zod';
import { chartMeasureKeys, chartNamingProblem, chartProblem, readOnlyQueryProblem, viaChartConfigSchema } from './analytics';
import { cappedQuery, classifyQueryError, QUERY_ROW_CAP, textNumberColumns, textNumbersProblem } from './analytics-query';
// Checks a dashboard's custom charts the way the dashboard draws them, so Via finds what's broken without the user
// having to describe it.
const chartCheckSchema = z.object({
    chartId: z.number(),
    title: z.string(),
    status: z.enum(['ok', 'empty', 'error']),
    rows: z.number().optional(),
    ms: z.number().optional().describe('How long its SQL took'),
    problem: z.string().optional()
});
export const checkAnalyticsViewToolSchema = {
    name: 'check_analytics_view',
    description: "Check every custom chart on the dashboard the user has open, the way the dashboard draws it: whether its SQL runs and how long it takes, whether it returns rows, whether it returns every column its config draws, and whether numbers come back as numbers. Includes errors the page itself ran into, such as running out of memory. Run it after saving charts, and when the user says a chart is empty or wrong. Built-in and markdown charts aren't checked.",
    inputSchema: z.object({ viewId: z.string().describe('The dashboard to check; it must be the one open') }),
    outputSchema: z.object({
        viewId: z.string(),
        dateRange: z.object({ from: z.string(), to: z.string() }),
        charts: z.array(chartCheckSchema)
    })
};
// A drilldown chart returns one row holding its rows in queryData, which the dashboard unwraps.
const drawnRows = (rows) => {
    const queryData = rows.length === 1 ? rows[0]?.queryData : undefined;
    return Array.isArray(queryData) ? queryData : rows;
};
/** Why the dashboard would draw a chart wrong from its SQL's result, or undefined when it draws right. */
export const drawingProblem = (sql, config, { fields, rows }) => {
    const drawn = drawnRows(rows);
    if (drawn !== rows) {
        return drawn.length === 0 ? undefined : chartProblem({ sql, config }, Object.keys(drawn[0] ?? {}));
    }
    const textNumbers = textNumberColumns(fields).filter((column) => chartMeasureKeys(config).includes(column));
    return (chartProblem({ sql, config }, fields.map(({ name }) => name)) ?? textNumbersProblem(textNumbers));
};
/** How a custom chart fares on the open dashboard; undefined for charts that run no SQL. */
export const checkChart = async (query, chart) => {
    const config = viaChartConfigSchema.safeParse(chart.chartConfig);
    if (chart.builtInKey || !config.success || config.data.type === 'markdown')
        return undefined;
    const checked = { chartId: chart.id, title: chart.chartTitle };
    const started = Date.now();
    try {
        const result = await query(chart.chartSql);
        const ms = Date.now() - started;
        const rows = drawnRows(result.rows).length;
        const problem = drawingProblem(chart.chartSql, config.data, result);
        if (problem)
            return { ...checked, status: 'error', rows, ms, problem };
        return { ...checked, status: rows === 0 ? 'empty' : 'ok', rows, ms };
    }
    catch (error) {
        return {
            ...checked,
            status: 'error',
            ms: Date.now() - started,
            problem: classifyQueryError(error).message
        };
    }
};
/**
 * What would stop save_chart saving a chart, and the rows it would draw. The query is the open dashboard's; markdown
 * charts need none.
 */
export const tryChart = async (query, input) => {
    const namingProblem = chartNamingProblem(input);
    if (namingProblem)
        return { problem: namingProblem, rows: [] };
    if (input.config.type === 'markdown')
        return { problem: chartProblem(input), rows: [] };
    if (!query)
        return { problem: 'Open the dashboard first, so its SQL can run before it is saved.', rows: [] };
    const queryProblem = readOnlyQueryProblem(input.sql);
    if (queryProblem)
        return { problem: queryProblem, rows: [] };
    try {
        const result = await query(cappedQuery(input.sql));
        return {
            problem: drawingProblem(input.sql, input.config, result),
            rows: result.rows.slice(0, QUERY_ROW_CAP)
        };
    }
    catch (error) {
        return { problem: classifyQueryError(error).message, rows: [] };
    }
};
//# sourceMappingURL=analytics-check.js.map